import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  SvgIcon,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import VideoPlayer from '../components/common/VideoPlayer';
import { CarouselElement } from '../types/shop';
import { colors } from '../styles/fontStyles';
import { styled } from '@mui/system';

interface MediaCarouselProps {
  videoConfiguration?: {
    containerWidth: string;
    videoHeight: string;
  };
  elements: CarouselElement[];
}

// White for the triangle and blue in the outer
const CustomPlayButton = ({ outerColor = colors.primaryBlue, innerColor = colors.white, size = 80 }) => (
  <SvgIcon sx={{ fontSize: size }}>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="48" fill={outerColor} />
      <path d="M35 25 L35 75 L75 50 Z" fill={innerColor} />
    </svg>
  </SvgIcon>
);

const StyledDialog = styled(Dialog)(({ theme }) => ({
  padding: '0 20px',
  '& .MuiDialog-paper': {
    maxWidth: '700px',
    backgroundColor: 'transparent !important',
    boxShadow: 'none',
    backgroundImage: 'none',
    overflowY: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      width: '0px',
      background: 'transparent',
    },
    '&-ms-overflow-style': 'none',
  },
  '& .MuiDialogContent-root': {
    overflowY: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      width: '0px',
      background: 'transparent',
    },
    '&-ms-overflow-style': 'none',
  },
  '& .MuiModal-backdrop': {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
  },
}));

const objectFit = 'cover';
const defaultVideoConfiguration = {
  containerWidth: '360px',
  videoHeight: '480px',
};

const MediaCarousel: React.FC<MediaCarouselProps> = ({
  elements,
  videoConfiguration = defaultVideoConfiguration,
}): JSX.Element => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayElements, setDisplayElements] = useState<CarouselElement[]>([]);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [showSingleElement, setShowSingleElement] = useState<boolean>(false);
  const [activeElement, setActiveElement] = useState<CarouselElement | null>(null);
  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (elements.length < 3) {
      console.log('MediaCarousel requires at least 3 images to render.');
      return;
    }

    const updateDisplayElements = () => {
      let newIndex = currentIndex;
      const nextElements = [];
      for (let i = 0; i < 3; i++) {
        newIndex = (newIndex + 1) % elements.length;
        nextElements.push(elements[newIndex]);
      }
      setDisplayElements(nextElements);
    };
    updateDisplayElements();
  }, [currentIndex, elements]);

  const handleShowAll = () => {
    setShowAll(true);
  };

  const handleCloseShowAll = () => {
    setShowAll(false);
  };

  const onCloseSingleElement = () => {
    setShowSingleElement(false);
    setActiveElement(null);
  };

  const getWidthFor = (index: number) => {
    const desktopWidths = ['150px', '100px', '70px'];
    const mobileWidths = ['100px', '80px', '40px'];
    if (isMobile) {
      return mobileWidths[index];
    }

    return desktopWidths[index];
  };

  const handleElementClick = (index: number) => {
    setCurrentIndex(index);
  };

  const onImageClick = (indexPosition: number) => {
    handleElementClick(displayElements[indexPosition].indexPosition);
  };

  const getMarginLeftForButton = (index: number) => {
    const desktopMarginLeft = ['42px', '15px', '0px'];
    const mobileMarginLeft = ['21px', '11px', '-12px'];
    if (isMobile) {
      return mobileMarginLeft[index];
    }

    return desktopMarginLeft[index];
  };

  const renderButtonIfVideo = (element: CarouselElement, index: number) => {
    if (!element.isVideo) return;

    return (
      <Button
        onClick={() => onImageClick(index)}
        sx={{
          marginLeft: getMarginLeftForButton(index),
          position: 'absolute',
          marginTop: '62px',
        }}
      >
        <CustomPlayButton size={isMobile ? 35 : 49} />
      </Button>
    );
  };

  const renderMainElement = () => {
    const activeElement = elements[currentIndex];
    if (activeElement.isVideo && activeElement.videoURL) {
      return (
        <VideoPlayer
          url={activeElement.videoURL}
          containerWidth={videoConfiguration.containerWidth}
          videoHeight={videoConfiguration.videoHeight}
        />
      );
    }
    return (
      <Box
        sx={{
          borderRadius: '20%',
          width: '800px',
          maxHeight: '600px',
        }}
      >
        <Box
          component="img"
          width={isMobile ? '100%' : 'auto'}
          src={activeElement.src}
          alt={activeElement.alt}
          onClick={() => onClickOpenSingleImageModal(activeElement)}
          sx={{
            width: '100%',
            mb: 3,
            borderRadius: '20px',
            ':hover': {
              cursor: 'pointer',
            },
            minHeight: '400px',
            maxHeight: '400px',
          }}
        />
      </Box>
    );
  };

  const onClickOpenSingleImageModal = (currentImage: CarouselElement) => {
    handleCloseShowAll();
    setActiveElement(currentImage);
    setShowSingleElement(true);
  };

  const onHandleNextElement = () => {
    const activeElementIndex = activeElement?.indexPosition || 0;
    const nextIndex = (activeElementIndex + 1) % elements.length;
    setActiveElement(elements[nextIndex]);
  };

  const onHandlePreviousElement = () => {
    const activeElementIndex = activeElement?.indexPosition || 0;
    if (activeElementIndex === 0) {
      const nextIndex = elements.length - 1;
      setActiveElement(elements[nextIndex]);
      return;
    }
    const nextIndex = (activeElementIndex - 1) % elements.length;
    setActiveElement(elements[nextIndex]);
  };

  const renderSingleElementInModal = () => {
    const videoHeight = isMobile ? '50vh' : '80vh';
    const videoWidth = isMobile ? '100%' : videoConfiguration.containerWidth;
    const marginBottom = isMobile ? '0px' : '48px';
    if (activeElement?.isVideo && activeElement?.videoURL) {
      return (
        <VideoPlayer
          url={activeElement?.videoURL}
          containerWidth={videoWidth}
          videoHeight={videoHeight}
          sxProps={{ marginBottom: marginBottom }}
        />
      );
    }

    return (
      <Box
        component="img"
        src={activeElement?.src}
        alt={activeElement?.alt}
        sx={{
          borderRadius: '20px',
          cursor: 'pointer',
          objectFit: objectFit,
          width: '100%',
        }}
      />
    );
  };

  const renderSingleImageModal = () => {
    return (
      <StyledDialog
        sx={{
          '& .MuiDialog-paper': {
            padding: '0 40px',
          },
        }}
        open={showSingleElement}
        onClose={onCloseSingleElement}
      >
        <IconButton
          aria-label="close"
          onClick={onCloseSingleElement}
          sx={(theme) => ({
            position: 'absolute',
            right: 0,
          })}
        >
          <CloseIcon fontSize="medium" sx={{ color: colors.white }} />
        </IconButton>
        <DialogContent
          sx={{
            paddingTop: '32px',
            backgroundColor: 'transparent !important',
            '& .MuiModal-root': {
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            },
          }}
        >
          {renderSingleElementInModal()}
          <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
            <Typography
              sx={{
                '&:hover': { cursor: 'pointer' },
              }}
              onClick={() => {
                handleShowAll();
                onCloseSingleElement();
              }}
            >
              Show all
            </Typography>
          </Box>

          <IconButton
            aria-label="back"
            onClick={onHandlePreviousElement}
            sx={(theme) => ({
              position: 'absolute',
              top: '50%',
              left: 0,
            })}
          >
            <ArrowBackIosNewIcon fontSize="small" sx={{ color: colors.white }} />
          </IconButton>
          <IconButton
            aria-label="next"
            onClick={onHandleNextElement}
            sx={(theme) => ({
              position: 'absolute',
              right: 0,
              top: '50%',
            })}
          >
            <ArrowForwardIosIcon fontSize="small" sx={{ color: colors.white }} />
          </IconButton>
        </DialogContent>
      </StyledDialog>
    );
  };

  const renderThumbnails = () => {
    if (displayElements.length === 0) {
      console.log('MediaCarousel - renderThumbnails - displayElements.length === 0 ');
    }
    return displayElements.map((element, index) => {
      let finalCoverProp = objectFit;
      if (element.isVideo) {
        finalCoverProp = 'none';
      }
      return (
        <Grid
          key={element.id}
          sx={{
            sm: { justifyContent: 'center' },
            md: { justifyContent: 'left' },
            margin: '5px',
            width: getWidthFor(index),
            transition: 'all 0.3s ease-out',
          }}
        >
          {renderButtonIfVideo(element, index)}
          <Box
            component="img"
            src={element.src}
            alt={element.alt}
            sx={{
              borderRadius: '20px',
              width: '100%',
              height: '200px',
              cursor: 'pointer',
              objectFit: finalCoverProp,
            }}
            onClick={() => onImageClick(index)}
          />
        </Grid>
      );
    });
  };

  const renderModalWithImages = () => {
    return (
      <StyledDialog
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: '750px',
          },
        }}
        open={showAll}
        scroll="paper"
        onClose={handleCloseShowAll}
        fullWidth
      >
        <IconButton
          aria-label="close"
          onClick={handleCloseShowAll}
          sx={(theme) => ({
            position: 'absolute',
            right: { xs: '0px', sm: '0px', md: '60px' },
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon fontSize="medium" sx={{ color: colors.white }} />
        </IconButton>
        <DialogContent
          sx={{
            paddingTop: '42px',
            backgroundColor: 'transparent !important',
            '& .MuiModal-root': {
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            },
          }}
        >
          <Grid container sx={{ justifyContent: 'center' }}>
            {elements.map((element, index) => (
              <Grid sx={{ margin: { sm: '0 0px' }, width: { xs: '90%', sm: '46%', md: '27%' } }} key={index}>
                {element.isVideo && (
                  <Button
                    onClick={() => onClickOpenSingleImageModal(element)}
                    sx={{
                      top: '72px',
                      marginLeft: { xs: '37%', md: '60px' },
                    }}
                  >
                    <CustomPlayButton size={49} />
                  </Button>
                )}
                <Box
                  component="img"
                  src={element.src}
                  alt={element.alt}
                  onClick={() => onClickOpenSingleImageModal(element)}
                  sx={{
                    marginTop: element.isVideo ? '-61px' : '0px',
                    borderRadius: '25px',
                    cursor: 'pointer',
                    height: '214px',
                    marginBottom: '10px',
                    objectFit: element.isVideo ? 'none' : objectFit,
                    width: '92%',
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </StyledDialog>
    );
  };

  if (!displayElements.length) return <></>;

  return (
    <Box>
      {renderModalWithImages()}
      {renderMainElement()}
      {renderSingleImageModal()}
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        <Grid
          container
          spacing={4}
          sx={{
            marginTop: '5px',
            marginLeft: { sm: '1px' },
            justifyContent: { xs: 'center', sm: 'left' },
          }}
        >
          {renderThumbnails()}
        </Grid>
        <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
          <Typography
            sx={{
              '&:hover': { cursor: 'pointer' },
            }}
            onClick={handleShowAll}
          >
            Show all
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default MediaCarousel;
